/**
 * Admin Portal Route Guard
 * Checks if user has one of the allowed admin-portal roles.
 * Moved from src/components/auth/ProtectedRoute.tsx
 */
import { ReactNode, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { internalAccountsApi } from '@/apps/admin/lib/internal-accounts-api';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useUserRole } from '@/apps/admin/hooks/useUserRole';
import type { UserRole } from '@/shared/config/platform-config';
import { portalPath } from '@/shared/app/portalPath';
import ModuleRoute, { moduleForPath } from '@/apps/admin/guards/ModuleRoute';

interface ProtectedAdminRouteProps {
  children: ReactNode;
  allowedRoles: UserRole[];
}

export default function ProtectedAdminRoute({ children, allowedRoles }: ProtectedAdminRouteProps) {
  const { role, loading } = useUserRole();
  const location = useLocation();
  const p = portalPath('admin');
  const [deactivated, setDeactivated] = useState<boolean | null>(null);

  // Block only when an internal_profiles row exists AND is_active = false.
  useEffect(() => {
    if (!role) return;
    let alive = true;
    (async () => {
      const { data: sess } = await supabase.auth.getUser();
      const uid = sess?.user?.id;
      if (!uid) { if (alive) setDeactivated(false); return; }
      const ownActive = await internalAccountsApi.getOwnActive(uid);
      if (!alive) return;
      if (ownActive === false) {
        setDeactivated(true);
        toast.error('Account deactivated');
        await supabase.auth.signOut();
      } else {
        setDeactivated(false);
      }
    })();
    return () => { alive = false; };
  }, [role]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (deactivated) {
    return <Navigate to={p('/login')} replace />;
  }

  if (!role) {
    return <Navigate to={p('/login')} state={{ from: location.pathname }} replace />;
  }

  if (!allowedRoles.includes(role)) {
    toast.error('You do not have permission to access this page');
    return <Navigate to={p('/dashboard')} replace />;
  }

  const moduleKey = moduleForPath(location.pathname);
  if (moduleKey) return <ModuleRoute module={moduleKey}>{children}</ModuleRoute>;
  return <>{children}</>;
}
